import { useEffect, useState } from "react";
import {
  Alert,
  Stack,
  Button,
  Dialog,
  TextField,
  Typography,
  IconButton,
  DialogTitle,
  DialogActions,
  DialogContent
} from "@material-ui/core";
import { Close, Restore } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { ListingPicturesVisibilityControls } from "./ListingPicturesVisibilityControls";
import { ListingWatermarkPanel } from "../watermarks/ListingWatermarkPanel";
import type {
  ListingVisibilityPicture,
  ListingPicturesVisibility
} from "./ListingPicturesVisibilityControls";
import type { ListingPriorEdits } from "utils/Haim/firestoreUtils";

type EditListingInMinisiteDialogProps = {
  open: boolean;
  /** When given, shows the listing's live watermark-removal status above the pictures. */
  listingId?: string;
  pictures: ListingVisibilityPicture[] | null;
  /** The listing's original description (the baseline a reset returns to). */
  description: string;
  /** The listing's current presentation (the realtor's global record), or null when none. */
  initialEdits: ListingPriorEdits | null;
  /** How many of the realtor's minisites hold this listing — "מופיע אצל N לקוחות". */
  minisiteCount?: number;
  /** How many keep a one-off exception this edit will NOT change. */
  customPresentationCount?: number;
  onConfirm: (edits: ListingPriorEdits) => void;
  onClose: () => void;
};

/**
 * How a realtor presents ONE listing — photo visibility and the description — across ALL of their
 * minisites (2026-09-30; it used to edit one contact's copy, from a per-listing screen, which is
 * exactly the confusion "edit once" exists to kill).
 *
 * Controlled: it performs no writes and hands the chosen values back via onConfirm, which saves
 * the realtor's global record and fans it out. "Reset to original" returns the listing to its own
 * defaults everywhere.
 */
export function EditListingInMinisiteDialog({
  open,
  listingId,
  pictures,
  description,
  initialEdits,
  minisiteCount = 0,
  customPresentationCount = 0,
  onConfirm,
  onClose
}: EditListingInMinisiteDialogProps): JSX.Element {
  const { t: translate } = useTranslation();

  const [mode, setMode] = useState<ListingPicturesVisibility>("all");
  const [hiddenPictures, setHiddenPictures] = useState<string[]>([]);
  const [descriptionOverride, setDescriptionOverride] = useState(description);

  // Seed local state from the minisite's current edits whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    setMode(initialEdits?.picturesVisibilityMode ?? "all");
    setHiddenPictures(initialEdits?.hiddenPictures ?? []);
    setDescriptionOverride(initialEdits?.descriptionOverride || description);
  }, [open, initialEdits, description]);

  const handleModeChange = (newMode: ListingPicturesVisibility): void => {
    // ⚠️ The mode and the hidden SET are independent — do NOT clear the set here. A realtor who
    // hand-picked eight photos, glanced at "show all" and switched back used to lose all eight,
    // with no undo. `mode` alone decides whether the set applies.
    setMode(newMode);
  };

  const handleTogglePicture = (src: string): void => {
    setHiddenPictures((prev) =>
      prev.includes(src) ? prev.filter((picture) => picture !== src) : [...prev, src]
    );
  };

  const handleReset = (): void => {
    setMode("all");
    setHiddenPictures([]);
    setDescriptionOverride(description);
  };

  const handleConfirm = (): void => {
    const trimmed = descriptionOverride.trim();
    // Treat "same as original" as no override, mirroring the per-minisite editor.
    const finalDescriptionOverride = trimmed === description.trim() ? "" : trimmed;

    onConfirm({
      picturesVisibilityMode: mode,
      // Carried whatever the mode: the set survives a flip so it comes back intact.
      hiddenPictures,
      descriptionOverride: finalDescriptionOverride
    });

    onClose();
  };

  const hasPictures = Boolean(pictures?.length);

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <DialogTitle sx={{ py: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">
            {translate("listingMatchToContactsPage.editListing.title")}
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack gap={3} sx={{ pt: 1 }}>
          {/*
            The scope, stated before anything can be typed. This dialog is reached from a
            per-LISTING screen and used to mean "edit for this one buyer", which is the confusion
            the feature exists to kill. Two short lines beat one long one for this audience: what
            happens, then the part nobody believes — that it reaches links already sent.
          */}
          <Alert severity="info" icon={false} sx={{ py: 1 }}>
            <Typography variant="subtitle2">עורכים פעם אחת — כל הלקוחות רואים את השינוי.</Typography>
            <Typography variant="caption" color="text.secondary">
              {minisiteCount > 0
                ? `הנכס מופיע אצל ${minisiteCount} לקוחות. השינוי יחול מיד גם על קישורים ששלחתם כבר.`
                : "השינוי יחול על כל לקוח שיקבל את הנכס מכאן והלאה."}
            </Typography>
            {customPresentationCount > 0 && (
              <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "warning.dark" }}>
                {`ל-${customPresentationCount} לקוחות יש גרסה מותאמת אישית שלא תשתנה מהעריכה הזו.`}
              </Typography>
            )}
          </Alert>
          <Stack gap={1}>
            <Typography variant="subtitle1">
              {translate("listingMatchToContactsPage.editListing.descriptionLabel")}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={10}
              value={descriptionOverride}
              placeholder={translate("listingsMatch.descriptionOverride")}
              inputProps={{ sx: { resize: "none" } }}
              onChange={({ target: { value } }) => setDescriptionOverride(value)}
            />
          </Stack>

          {/* Per-listing watermark removal (Haim: "remove watermark on pictures from
              specific listings only ... when editing the listing in the minisite").
              Deliberately OUTSIDE the `hasPictures` gate and above the visibility
              controls: the previous ListingWatermarkStatusChip rendered `null` while
              loading and for listings with no marketplace doc, so the entry point
              silently vanished. The panel always says something. */}
          {listingId && <ListingWatermarkPanel listingId={listingId} />}

          {hasPictures && (
            <Stack gap={1.5}>
              <ListingPicturesVisibilityControls
                pictures={pictures}
                mode={mode}
                hiddenPictures={hiddenPictures}
                onModeChange={handleModeChange}
                onTogglePicture={handleTogglePicture}
              />
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
        <Button color="inherit" startIcon={<Restore />} onClick={handleReset}>
          {translate("listingMatchToContactsPage.editListing.resetToOriginal")}
        </Button>
        <Stack direction="row" gap={1}>
          <Button color="inherit" onClick={onClose}>
            {translate("general.cancel")}
          </Button>
          <Button variant="contained" onClick={handleConfirm}>
            {translate("listingMatchToContactsPage.editListing.save")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
